'use client';

/**
 * The lesson player — where the actual learning happens.
 *
 * Input handling: characters come from the hidden input's `change` event
 * rather than from `keydown`. That is deliberate. Physical keyboards fire both,
 * but soft keyboards on iOS and Android frequently report `key: 'Unidentified'`
 * on keydown, so a keydown-only implementation silently fails on tablets — the
 * exact device most primary schools hand to children. `keydown` is still used,
 * but only for Backspace, Escape and the key-press animation.
 *
 * Pedagogy encoded here:
 *   • strict lessons block on a wrong key, so a beginner cannot outrun their
 *     fingers and drill the wrong movement
 *   • lives exist only on boss lessons, so failure is never possible while
 *     something new is being learned
 *   • the timer starts on the first keystroke, never on page load, so a child
 *     who wanders off is not punished
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Eye,
  Keyboard as KeyboardIcon,
  Hand,
  Volume2,
  VolumeX,
  Pause,
  Play,
} from 'lucide-react';
import type { EngineSnapshot, LessonDef, LevelDef, LessonScore } from '../types';
import { applyBackspace, applyKey, completion, createSnapshot } from '../lib/engine';
import { scoreLesson } from '../lib/metrics';
import { generateLesson } from '../lib/lessonContent';
import { fingerHint, keyForChar, charLabel } from '../lib/keyboard';
import { kidsAudio } from '../lib/audio';
import { useKids } from '../context/KidsProvider';
import KidsKeyboard from './Keyboard';
import HandsGuide from './HandsGuide';
import { Confetti, Floaters, useFloaters } from './Celebration';
import LessonSummary, { type SummaryData } from './LessonSummary';
import PromptTiles from './PromptTiles';

type Phase = 'ready' | 'typing' | 'paused' | 'failed' | 'done';

const BOSS_LIVES = 5;
const BOSS_HINTS = 3;

/** Who sits on the next key. Falls back to the house mascot. */
const DEFAULT_MASCOT = '🐵';
const PETS: Record<string, string> = {
  cat: '🐈',
  dog: '🐕',
  dragon: '🐉',
  robot: '🤖',
};

/* ────────────────────────────────────────────────────────── prompt text ─── */

const PromptChar = React.memo(function PromptChar({
  char,
  state,
  innerRef,
}: {
  char: string;
  state: EngineSnapshot['states'][number];
  innerRef?: React.Ref<HTMLSpanElement>;
}) {
  const isSpace = char === ' ';
  return (
    <span
      ref={innerRef}
      className={`k-char k-char-${state}${isSpace ? ' k-char-space' : ''}`}
      data-state={state}
    >
      {isSpace ? ' ' : char}
    </span>
  );
});

/* ──────────────────────────────────────────────────────────── the player ── */

export default function LessonPlayer({
  lesson,
  level,
  /** Bumped by the caller to force a brand-new exercise. */
  variant = 0,
}: {
  lesson: LessonDef;
  level: LevelDef;
  variant?: number;
}) {
  const { profile, settings, finishLesson, updateSettings } = useKids();

  const [attempt, setAttempt] = useState(variant);
  const generated = useMemo(() => generateLesson(lesson, attempt), [lesson, attempt]);

  const [snap, setSnap] = useState<EngineSnapshot>(() => createSnapshot(generated.text));
  const [phase, setPhase] = useState<Phase>('ready');
  const [pressed, setPressed] = useState<Set<string>>(() => new Set());
  const [wrongKeyId, setWrongKeyId] = useState<string | null>(null);
  const [lives, setLives] = useState(BOSS_LIVES);
  const [hintsLeft, setHintsLeft] = useState(BOSS_HINTS);
  const [hintVisible, setHintVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(lesson.durationSec ?? 0);
  const [confettiKey, setConfettiKey] = useState(0);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  /**
   * Whether the hidden input actually holds focus. Browsers can refuse a
   * programmatic focus before any user gesture, and a child typing into a page
   * that is not listening has no way to tell. This is the only thing that
   * covers the exercise, and only when it genuinely cannot receive keystrokes.
   */
  const [hasFocus, setHasFocus] = useState(false);

  const { items: floaters, push: pushFloater } = useFloaters();

  const inputRef = useRef<HTMLInputElement>(null);
  const currentCharRef = useRef<HTMLSpanElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastLineTop = useRef(-1);
  const snapRef = useRef(snap);
  snapRef.current = snap;

  const isBoss = lesson.kind === 'boss';
  const isTimed = Boolean(lesson.durationSec);
  const nextChar = snap.cursor < snap.target.length ? snap.target[snap.cursor] : undefined;
  const showKeyboard = settings.keyboard && (!isBoss || hintVisible);
  const showHands = settings.hands && (!isBoss || hintVisible);

  // Key drills get the big-tile prompt; anything with real words keeps flowing
  // text, because from Level 6 the child is reading as well as pressing.
  const useTiles = lesson.kind === 'keys';

  /** The target split into words, each keeping its trailing space. */
  const words = useMemo(() => {
    const groups: { start: number; text: string }[] = [];
    let start = 0;
    for (const part of snap.target.split(/(?<=\s)/)) {
      groups.push({ start, text: part });
      start += part.length;
    }
    return groups;
  }, [snap.target]);

  /**
   * Only shown when the exercise genuinely cannot receive keystrokes — never
   * as a "press start" gate. `paused`, `failed` and `done` draw their own
   * screens, so this is specifically the case where focus was refused or
   * stolen while the child was trying to work.
   */
  const needsFocusPrompt = !hasFocus && (phase === 'ready' || phase === 'typing');

  // The mascot rides the next key. It is the child's pet once they own one,
  // which quietly connects the reward shop to the thing they do every day.
  const mascot = PETS[profile?.avatar.pet ?? 'none'] ?? DEFAULT_MASCOT;

  /* ── focus management ──────────────────────────────────────────────────
     The input is invisible but must hold focus for keystrokes to arrive.
     preventScroll stops the page jumping when focus lands. */
  const focusInput = useCallback(() => {
    // Focus can be refused for a frame or two right after navigation, so retry
    // across a few frames rather than assuming the first attempt landed.
    let attempt = 0;
    const tryFocus = () => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      if (document.activeElement === el) setHasFocus(true);
      else if (attempt++ < 6) window.setTimeout(() => requestAnimationFrame(tryFocus), 50);
    };
    requestAnimationFrame(tryFocus);
  }, []);

  useEffect(() => {
    if (phase === 'ready' || phase === 'typing') focusInput();
  }, [phase, focusInput]);

  /* ── reset when the exercise changes ───────────────────────────────────── */
  useEffect(() => {
    setSnap(createSnapshot(generated.text));
    setPhase('ready');
    setLives(BOSS_LIVES);
    setHintsLeft(BOSS_HINTS);
    setHintVisible(false);
    setTimeLeft(lesson.durationSec ?? 0);
    setSummary(null);
    lastLineTop.current = -1;
  }, [generated.text, lesson.durationSec]);

  /* ── spoken coaching for the intro ─────────────────────────────────────── */
  useEffect(() => {
    if (phase === 'ready' && settings.voice) kidsAudio.speak(lesson.coach, { interrupt: true });
    return () => kidsAudio.stopSpeech();
  }, [phase, lesson.coach, settings.voice]);

  /* ── finishing ─────────────────────────────────────────────────────────── */

  const finish = useCallback(
    (final: EngineSnapshot) => {
      const score: LessonScore = scoreLesson(final, lesson, Date.now());
      const outcome = finishLesson(lesson.id, score, final.keyStats);

      kidsAudio.play(score.stars === 3 ? 'levelUp' : 'complete');
      if (score.stars >= 2) setConfettiKey((k) => k + 1);
      if (settings.voice) {
        kidsAudio.speak(
          score.stars === 3
            ? 'Three stars! Outstanding work.'
            : score.stars === 2
              ? 'Two stars. Nicely done.'
              : 'Lesson complete. Keep practising.',
          { interrupt: true }
        );
      }

      setSummary({ score, outcome, lesson, level });
      setPhase('done');
    },
    [finishLesson, lesson, level, settings.voice]
  );

  /* ── the core keystroke path ───────────────────────────────────────────── */

  const handleChar = useCallback(
    (char: string) => {
      const current = snapRef.current;
      if (current.finished) return;

      const wasIdle = current.startedAt === 0;
      const next = applyKey(current, char, { strict: lesson.strict, now: Date.now() });
      if (next === current) return;

      const missed = next.mistakes > current.mistakes;

      if (missed) {
        kidsAudio.play('error');
        const key = keyForChar(char);
        setWrongKeyId(key?.id ?? null);
        window.setTimeout(() => setWrongKeyId(null), 280);

        if (isBoss) {
          setLives((remaining) => {
            const left = remaining - 1;
            if (left <= 0) {
              setPhase('failed');
              kidsAudio.play('error');
            }
            return Math.max(0, left);
          });
        }
      } else {
        kidsAudio.play(char === ' ' ? 'space' : 'key');
        // A small reward every 25 clean characters keeps momentum visible
        // without turning every keystroke into a firework.
        if (next.correctFirstTry > 0 && next.correctFirstTry % 25 === 0) {
          pushFloater(
            '+25 clean!',
            'var(--k-mint)',
            40 + ((next.correctFirstTry / 25) % 3) * 10,
            60
          );
          kidsAudio.play('correct');
        }
      }

      setSnap(next);
      if (wasIdle) setPhase('typing');
      if (next.finished) finish(next);
    },
    [finish, isBoss, lesson.strict, pushFloater]
  );

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (phase === 'paused' || phase === 'done' || phase === 'failed') {
        event.target.value = '';
        return;
      }
      const value = event.target.value;
      event.target.value = '';
      for (const char of value) handleChar(char);
    },
    [handleChar, phase]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (phase === 'typing') setPhase('paused');
        return;
      }
      if (event.key === 'Backspace') {
        event.preventDefault();
        setSnap((s) => applyBackspace(s, { strict: lesson.strict, now: Date.now() }));
        return;
      }
      // Key-press animation only — characters are handled by onChange so soft
      // keyboards behave identically to physical ones.
      const key = event.key.length === 1 ? keyForChar(event.key) : null;
      const id =
        key?.id ??
        (event.key === 'Shift' ? `Shift${event.location === 2 ? 'Right' : 'Left'}` : null);
      if (id) setPressed((prev) => new Set(prev).add(id));
    },
    [lesson.strict, phase]
  );

  const onKeyUp = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    const key = event.key.length === 1 ? keyForChar(event.key) : null;
    const id =
      key?.id ?? (event.key === 'Shift' ? `Shift${event.location === 2 ? 'Right' : 'Left'}` : null);
    if (!id) return;
    setPressed((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  /* No live speed or accuracy readout, and therefore no polling timer.
     Showing a child their words-per-minute mid-lesson teaches them to watch a
     number instead of the keyboard, and speed is not what they are here to
     learn. Both figures are still recorded and surfaced to grown-ups on
     /kids/progress and /kids/parents. */

  /* ── countdown for timed lessons ───────────────────────────────────────── */
  useEffect(() => {
    if (phase !== 'typing' || !isTimed) return;
    const id = window.setInterval(() => {
      setTimeLeft((remaining) => {
        if (remaining <= 1) {
          window.clearInterval(id);
          finish(snapRef.current);
          return 0;
        }
        return remaining - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, isTimed, finish]);

  /* ── pause when the tab loses focus ────────────────────────────────────── */
  useEffect(() => {
    if (phase !== 'typing') return;
    const onHide = () => document.hidden && setPhase('paused');
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [phase]);

  /* ── keep the active line in view ──────────────────────────────────────
     Only scrolls when the cursor moves to a new line, so the text does not
     creep sideways on every keystroke. */
  useEffect(() => {
    const span = currentCharRef.current;
    const container = scrollRef.current;
    if (!span || !container) return;
    const top = span.offsetTop;
    if (top === lastLineTop.current) return;
    lastLineTop.current = top;
    container.scrollTo({
      top: Math.max(0, top - container.clientHeight / 2 + span.offsetHeight),
      behavior: 'smooth',
    });
  }, [snap.cursor]);

  /* ── actions ───────────────────────────────────────────────────────────── */

  const retry = useCallback(() => {
    setAttempt((n) => n + 1);
    window.requestAnimationFrame(focusInput);
  }, [focusInput]);

  const useHint = useCallback(() => {
    if (hintsLeft <= 0) return;
    setHintsLeft((n) => n - 1);
    setHintVisible(true);
    if (nextChar) kidsAudio.speak(fingerHint(nextChar), { interrupt: true });
    window.setTimeout(() => setHintVisible(false), 4000);
  }, [hintsLeft, nextChar]);

  /* ── render ────────────────────────────────────────────────────────────── */

  if (phase === 'done' && summary) {
    return <LessonSummary data={summary} onRetry={retry} confettiKey={confettiKey} />;
  }

  const progress = completion(snap);

  return (
    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <Confetti trigger={confettiKey} />
      <Floaters items={floaters} />

      {/* ── header ───────────────────────────────────────────────────────
          Four things, maximum: where you are, how to get out, sound, pause.
          Everything else a child might need lives in Settings. */}
      <div className="flex items-center gap-2 sm:gap-3 mb-2">
        <Link
          href={`/kids/journey/${level.slug}`}
          className="k-btn k-btn-ghost k-btn-sm shrink-0"
          aria-label={`Back to ${level.title}`}
        >
          <ArrowLeft size={19} aria-hidden="true" />
          <span className="hidden sm:inline">Back</span>
        </Link>

        <div className="min-w-0 flex-1 text-center">
          <p className="k-eyebrow truncate">
            {level.emoji} Level {level.index}
          </p>
          <h1 className="text-base sm:text-lg truncate">{lesson.title}</h1>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {/* A countdown only exists where the clock is the point of the
              exercise. Everywhere else there is no timer on screen at all. */}
          {isTimed && (
            <span
              className="k-pill tabular-nums"
              style={{
                background: timeLeft <= 10 ? 'var(--incorrect-soft)' : 'var(--k-mango-soft)',
                color: timeLeft <= 10 ? 'var(--incorrect)' : 'var(--k-mango)',
                fontSize: '1rem',
              }}
              aria-label={`${timeLeft} seconds left`}
            >
              ⏱ {timeLeft}
            </span>
          )}

          {/* Hearts only on boss lessons — nothing can be failed while
              something new is still being learned. */}
          {isBoss && (
            <span className="text-base" aria-label={`${lives} lives left`}>
              {'❤️'.repeat(lives)}
              {'🤍'.repeat(Math.max(0, BOSS_LIVES - lives))}
            </span>
          )}

          <button
            type="button"
            onClick={() => {
              const next = !settings.sound;
              // setEnabled flips the flag on the audio engine *and* plays a
              // blip, both inside this click. Waiting for React state to
              // round-trip would miss the user gesture the AudioContext needs,
              // and a silent sound button is indistinguishable from a broken one.
              kidsAudio.setEnabled(next);
              updateSettings({ sound: next });
            }}
            className="k-btn k-btn-ghost k-btn-sm"
            aria-label={settings.sound ? 'Turn sound off' : 'Turn sound on'}
            aria-pressed={settings.sound}
          >
            {settings.sound ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
          {phase === 'typing' && (
            <button
              type="button"
              onClick={() => setPhase('paused')}
              className="k-btn k-btn-ghost k-btn-sm"
              aria-label="Pause"
            >
              <Pause size={20} />
            </button>
          )}
        </div>
      </div>

      {/* The coach line lives in the flow, above the exercise — not on top of
          it. One short sentence, always visible, blocking nothing. */}
      <p className="text-center text-muted-foreground px-2 mb-2 text-sm sm:text-base">
        {lesson.coach}
      </p>

      {/* ── the exercise ───────────────────────────────────────────────── */}
      <div className="k-stage mt-1 relative">
        {generated.story && (
          <div
            className="px-5 py-3 flex items-center gap-2 text-sm font-extrabold"
            style={{ borderBottom: '2px solid var(--border)', background: 'var(--muted)' }}
          >
            <span aria-hidden="true">{generated.story.emoji}</span>
            {generated.story.title}
          </div>
        )}

        {/* Screen-reader users get the exercise as plain text once. The visual
            rendering below is decorative grading, hidden from the tree. */}
        <span className="sr-only">{snap.target}</span>

        {useTiles ? (
          /* Key drills render as a short window of huge tiles. A wall of
             monospaced text is the wrong shape for a child who is still
             learning where one letter lives. */
          <div
            className="py-4 px-2"
            onClick={() => {
              kidsAudio.unlock();
              focusInput();
            }}
          >
            <PromptTiles target={snap.target} states={snap.states} cursor={snap.cursor} />
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="k-prompt px-5 sm:px-7 py-6 overflow-y-auto"
            style={{ maxHeight: 'min(34vh, 240px)' }}
            onClick={() => {
              kidsAudio.unlock();
              focusInput();
            }}
          >
            <span aria-hidden="true">
              {/* Grouped by word, not by character. Rendering 300 loose inline
                  spans lets the browser break a line *inside* a word — "has"
                  wrapping as "ha" / "s" — which is unreadable when the word is
                  the thing you are trying to type. Each word is one
                  inline-block, so lines only ever break between words. */}
              {words.map((word) => (
                <span className="k-word" key={word.start}>
                  {word.text.split('').map((char, i) => {
                    const index = word.start + i;
                    return (
                      <PromptChar
                        key={index}
                        char={char}
                        state={snap.states[index]}
                        innerRef={index === snap.cursor ? currentCharRef : undefined}
                      />
                    );
                  })}
                </span>
              ))}
            </span>
          </div>
        )}

        {/* The keystroke sink. Invisible, but focusable and labelled. */}
        <input
          ref={inputRef}
          type="text"
          defaultValue=""
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onFocus={() => setHasFocus(true)}
          onBlur={() => {
            setHasFocus(false);
            if (phase === 'typing') window.setTimeout(focusInput, 0);
          }}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-label="Type the text shown above"
          className="absolute opacity-0 pointer-events-none"
          style={{ width: 1, height: 1, top: 0, left: 0 }}
        />

        {/* ── overlays ───────────────────────────────────────────────────
            There is deliberately no "ready" overlay. It used to sit over the
            exercise with a translucent background, so the tiles ghosted
            through the text and it read as a rendering fault — and it made a
            child click a button before they were allowed to begin. The
            exercise is simply there, focused, from the first frame. The only
            thing that ever covers it now is a genuine interruption: a pause,
            or focus actually being lost. */}
        {needsFocusPrompt && (
          <button
            type="button"
            onClick={focusInput}
            className="absolute inset-0 z-30 grid place-items-center k-anim-pop"
            style={{ background: 'var(--card)' }}
          >
            <span className="text-center px-6">
              <span className="block" style={{ fontSize: 40 }} aria-hidden="true">
                👆
              </span>
              <span className="block font-extrabold text-lg mt-1">Tap here to type</span>
            </span>
          </button>
        )}

        {phase === 'paused' && (
          <Overlay>
            <div className="text-center px-6">
              <div style={{ fontSize: 44 }} aria-hidden="true">
                ⏸️
              </div>
              <h2 className="mt-2 text-xl">Paused</h2>
              <p className="text-muted-foreground mt-1">Take your time.</p>
              <button
                type="button"
                onClick={() => {
                  setPhase('typing');
                  focusInput();
                }}
                className="k-btn k-btn-primary mt-5"
              >
                <Play size={18} aria-hidden="true" /> Keep going
              </button>
            </div>
          </Overlay>
        )}

        {phase === 'failed' && (
          <Overlay>
            <div className="text-center px-6">
              <div style={{ fontSize: 44 }} aria-hidden="true">
                💫
              </div>
              <h2 className="mt-2 text-xl">Out of lives — but not out of tries</h2>
              <p className="text-muted-foreground mt-1 max-w-sm">
                Boss lessons are meant to be hard. Warm up on an earlier lesson, then come back.
              </p>
              <div className="flex flex-wrap gap-2 justify-center mt-5">
                <button type="button" onClick={retry} className="k-btn k-btn-primary">
                  Try again
                </button>
                <Link href={`/kids/journey/${level.slug}`} className="k-btn k-btn-secondary">
                  Back to level
                </Link>
              </div>
            </div>
          </Overlay>
        )}
      </div>

      {/* ── guidance ─────────────────────────────────────────────────────
          Stacked, not side by side. The child's eye should travel straight
          down: what to press → where it is → which finger. Putting the hands
          beside the keyboard makes that a diagonal, and halves both. */}
      {showKeyboard && (
        <div className="mt-5">
          <KidsKeyboard
            nextChar={nextChar}
            pressed={pressed}
            wrongKeyId={wrongKeyId}
            availableKeys={level.cumulativeKeys}
            showFingerColors
            vivid
            mascot={mascot}
            skin={profile?.equipped.keyboardSkin}
            size="xl"
          />
        </div>
      )}

      {showHands && <HandsGuide char={nextChar} className="mx-auto max-w-[340px] w-full mt-4" />}

      {/* One quiet line for "how far along am I" — no percentage, no clock. */}
      <div
        className="k-rail-thin mt-6 max-w-2xl mx-auto"
        role="progressbar"
        aria-valuenow={Math.round(progress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Lesson progress"
      >
        <span style={{ width: `${progress * 100}%` }} />
      </div>

      {isBoss && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={useHint}
            disabled={hintsLeft <= 0}
            className="k-btn k-btn-secondary k-btn-sm"
          >
            <Eye size={16} aria-hidden="true" />
            Show me ({hintsLeft} left)
          </button>
          <p className="text-xs text-muted-foreground">
            Boss lessons hide the keyboard. Use a hint if you get stuck.
          </p>
        </div>
      )}

      {/* Live region so screen-reader users hear misses without watching colour. */}
      <p className="sr-only" role="status" aria-live="assertive">
        {snap.lastWrong
          ? `Wrong key. Press ${charLabel(snap.lastWrong.expected)}.`
          : nextChar
            ? `Next: ${charLabel(nextChar)}`
            : ''}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── helpers ── */

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="absolute inset-0 grid place-items-center z-30 k-anim-pop"
      // Fully opaque. A translucent scrim let the exercise show through the
      // message and made both unreadable.
      style={{ background: 'var(--card)' }}
    >
      {children}
    </div>
  );
}
